import { useDisclosure } from "@mantine/hooks";
import cx from "classnames";
import { useMemo, useState } from "react";
import { t } from "ttag";

import { QuestionDownloadWidget } from "metabase/common/components/QuestionDownloadWidget";
import { useDownloadData } from "metabase/common/components/QuestionDownloadWidget/use-download-data";
import { useSelector } from "metabase/redux";
import { getMetadata } from "metabase/selectors/metadata";
import { ActionIcon, Icon, Menu } from "metabase/ui";
import { checkNotNull } from "metabase/utils/types";
import { SAVING_DOM_IMAGE_HIDDEN_CLASS } from "metabase/visualizations/lib/save-chart-image";
import Question from "metabase-lib/v1/Question";
import type { Card, Dataset } from "metabase-types/api";

import { useExternalCardData } from "./ExternalCardDataContext";

type ExternalDocumentCardMenuProps = {
  card: Card;
  dataset: Dataset;
};

export const ExternalDocumentCardMenu = ({
  card,
  dataset,
}: ExternalDocumentCardMenuProps) => {
  const externalCardData = useExternalCardData();
  const [menuView, setMenuView] = useState<string | null>(null);
  const [isOpen, { close, toggle }] = useDisclosure(false, {
    onClose: () => {
      setMenuView(null);
    },
  });

  const metadata = useSelector(getMetadata);
  const question = useMemo(
    () => new Question(card, metadata),
    [card, metadata],
  );

  const [{ loading: isDownloadingData }, handleDownload] = useDownloadData({
    question: question,
    result: dataset,
    documentUuid: checkNotNull(externalCardData).documentUuid,
  });

  return (
    <Menu
      withinPortal
      offset={4}
      position="bottom-end"
      opened={isOpen}
      onClose={close}
    >
      <Menu.Target>
        <ActionIcon
          size="xs"
          className={cx({
            [SAVING_DOM_IMAGE_HIDDEN_CLASS]: true,
          })}
          onClick={toggle}
          data-testid="public-document-card-menu"
          aria-label="ellipsis"
        >
          <Icon name="ellipsis" />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {menuView === "downloads" ? (
          <QuestionDownloadWidget
            question={question}
            result={dataset}
            onDownload={async (opts) => {
              close();

              await handleDownload(opts);
            }}
          />
        ) : (
          <Menu.Item
            fw="bold"
            leftSection={<Icon name="download" aria-hidden />}
            aria-label={
              isDownloadingData ? t`Downloading…` : t`Download results`
            }
            disabled={isDownloadingData}
            closeMenuOnClick={false}
            onClick={() => {
              setMenuView("downloads");
            }}
          >
            {isDownloadingData ? t`Downloading…` : t`Download results`}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
